import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import dayjs, { Dayjs } from 'dayjs';
import { t } from 'i18next';
import { omit } from 'lodash-es';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import DateInput from '~components/FormFields/v2/DateInput';
import Label from '~components/FormFields/v2/Label';
import SelectInput from '~components/FormFields/v2/SelectInput';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { setSettlementSchemaDefaultValues } from '~formsShared/components/SettlementFormFields';
import { PaymentTerm } from '~hooks/usePaymentTerms/models';
import usePaymentTerms from '~hooks/usePaymentTerms/usePaymentTerms';
import { Settlement } from '~hooks/useSettlements/models';
import useSettlements from '~hooks/useSettlements/useSettlements';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import {
  buildPaymentTermOptions,
  dueDateMatchesPaymentTerm,
  NO_PAYMENT_TERM,
} from '~utils/settlements/paymentTerms';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { EM_DASH } from './constants';

interface SettlementDueDateProps {
  isReadOnly?: boolean;
  onSettlementChange: (settlement: Settlement) => void;
  settlement: Settlement;
}

interface SettlementDueDateFormValues {
  dueDate: Dayjs | null;
  paymentTermId: string;
}

const SettlementDueDate = ({
  isReadOnly = false,
  onSettlementChange,
  settlement,
}: SettlementDueDateProps) => {
  const savedPaymentTermId = settlement.paymentTermId ?? NO_PAYMENT_TERM;
  const { updateSettlement } = useSettlements();
  const { userStore, toasterStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const { fetchAll } = usePaymentTerms();
  const [paymentTerms, setPaymentTerms] = useState<PaymentTerm[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const { control, handleSubmit, reset, setError, setValue, watch } =
    useForm<SettlementDueDateFormValues>({
      defaultValues: {
        dueDate: settlement.dueDate,
        paymentTermId: savedPaymentTermId,
      },
    });

  useEffect(() => {
    if (!companyId) {
      return;
    }

    let isCurrent = true;

    const loadPaymentTerms = async () => {
      const terms = await fetchAll(companyId);

      // Switching companies leaves this card mounted, so a slower response for the
      // company the user just left must not replace the terms for the current one.
      if (isCurrent) {
        setPaymentTerms(terms);
      }
    };

    loadPaymentTerms();

    return () => {
      isCurrent = false;
    };
  }, [companyId, fetchAll]);

  const watchedPaymentTermId = watch('paymentTermId');
  const previousPaymentTermIdRef = useRef(savedPaymentTermId);

  const startEditing = () => {
    previousPaymentTermIdRef.current = savedPaymentTermId;
    reset({
      dueDate: settlement.dueDate,
      paymentTermId: savedPaymentTermId,
    });
    setIsEditing(true);
  };

  // Choosing a term defaults the due date to the settlement's creation date plus the
  // term's due days; the user can still edit the date afterwards.
  useEffect(() => {
    if (watchedPaymentTermId === previousPaymentTermIdRef.current) {
      return;
    }

    previousPaymentTermIdRef.current = watchedPaymentTermId;

    const dueDays = paymentTerms.find(
      (term) => term.id === watchedPaymentTermId,
    )?.dueDays;

    // due on receipt is a real term whose due_days is zero, so this cannot be a truthy check
    if (dueDays !== null && dueDays !== undefined) {
      setValue('dueDate', dayjs.tz(settlement.createdAt).add(dueDays, 'day'));
    }
  }, [paymentTerms, setValue, settlement.createdAt, watchedPaymentTermId]);

  const onSave = handleSubmit(async (values) => {
    if (!settlement.id) {
      return;
    }

    // A half-typed date arrives as an invalid Dayjs and would serialize to the literal
    // string "Invalid Date".
    if (values.dueDate && !values.dueDate.isValid()) {
      setError('dueDate', { message: t('form_validation_errors.invalid_date') });

      return;
    }

    if (!settlement.editable) {
      toasterStore.push(
        alert(t('error_messages.settlements.failed_to_update_locked'), AlertTypes.error),
      );

      return;
    }

    // While the date still matches the term's derived default, omit it so the server
    // computes the date itself and timezone differences can never drift the stored value;
    // a date the user actually changed travels as an explicit override.
    const selectedTerm = paymentTerms.find((term) => term.id === values.paymentTermId);
    const dueDateMatchesTermDerivedDate = dueDateMatchesPaymentTerm(
      values.dueDate,
      selectedTerm,
      settlement.createdAt,
    );

    setIsSaving(true);
    try {
      const updatedSettlement = await updateSettlement(companyId, settlement.id, {
        ...omit(setSettlementSchemaDefaultValues(undefined, settlement), 'notes'),
        dueDate: dueDateMatchesTermDerivedDate ? undefined : values.dueDate,
        paymentTermId: values.paymentTermId || null,
      });
      onSettlementChange(updatedSettlement);
      setIsEditing(false);
    } catch {
      // Error already handled in the hook.
    } finally {
      setIsSaving(false);
    }
  });

  const paymentTermOptions = buildPaymentTermOptions({
    paymentTerms,
    savedPaymentTermId,
  });

  return (
    <Box
      sx={{
        border: (theme) => `1px solid ${theme.semantic.border}`,
        borderRadius: '6px',
        backgroundColor: (theme) => theme.semantic.surfaceRaised,
        p: 2,
        minWidth: '300px',
      }}
    >
      {isEditing ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <SelectInput
            control={control}
            displayEmpty
            label={t('form_fields.payment_terms')}
            name="paymentTermId"
            options={paymentTermOptions}
          />
          <DateInput
            control={control}
            isClearable
            label={t('form_fields.due_date')}
            name="dueDate"
          />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
            }}
          >
            <SmallButton
              color="secondary"
              variant="outlined"
              startIcon={<CloseIcon />}
              disabled={isSaving}
              onClick={() => setIsEditing(false)}
            >
              {t('actions.cancel')}
            </SmallButton>
            <SmallLoadingButton
              color="brandV2Yellow"
              startIcon={<CheckIcon />}
              loading={isSaving}
              onClick={onSave}
            >
              {t('actions.save')}
            </SmallLoadingButton>
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.25,
              }}
            >
              <Label>{t('form_fields.payment_terms')}</Label>
              <Typography variant="body2">
                {settlement.paymentTermName ?? EM_DASH}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.25,
              }}
            >
              <Label>{t('form_fields.due_date')}</Label>
              <Typography variant="body2">
                {settlement.dueDate ? settlement.dueDate.format('MM/DD/YYYY') : EM_DASH}
              </Typography>
            </Box>
          </Box>

          {settlement.editable && !isReadOnly && (
            <IconButton
              aria-label={t('actions.edit')}
              size="small"
              onClick={startEditing}
            >
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      )}
    </Box>
  );
};

export default SettlementDueDate;
